import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ShieldCheck,
  Leaf,
  Truck,
  Sparkles,
  Star,
  Check,
  Gift,
  Clock,
  ArrowRight,
  Award,
  Heart,
} from "lucide-react";
import KlaviyoEmbed from "@/components/KlaviyoEmbed";
import CountdownTimer from "@/components/CountdownTimer";
import useReveal from "@/hooks/useReveal";
import {
  BRAND_ASSETS,
  BRANDS,
  ALL_BRANDS,
  TESTIMONIALS,
} from "@/lib/assets";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function useStats() {
  const [stats, setStats] = useState({
    total_entries: 0,
    spots_remaining: 1000,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/giveaway/stats`);
        if (mounted) setStats(data);
      } catch (e) {
        // silent fallback
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return stats;
}

function useSticky() {
  const [show, setShow] = useState(false);
  const hideBelow = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setShow(y > 560 && y < document.body.scrollHeight - 1200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return { show, hideBelow };
}

/* ---------- Sections ---------- */

function NavBar() {
  return (
    <header
      data-testid="site-navbar"
      className="relative z-20 border-b border-[#ececec] bg-white/80 backdrop-blur-sm"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
        <a
          href="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 shrink-0"
          aria-label="The Natural"
        >
          <img
            src={BRAND_ASSETS.site_logo}
            alt="The Natural"
            className="h-7 sm:h-8 w-auto object-contain"
          />
        </a>

        <div className="hidden md:flex items-center gap-2 text-[13px] text-[#4b5563]">
          <ShieldCheck size={14} className="text-[#2f5a3f]" />
          <span>Trusted by 50,000+ wellness customers</span>
        </div>

        <a
          href="#enter"
          data-testid="nav-cta"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#2f5a3f] text-white text-[12px] sm:text-[13px] font-semibold px-3.5 sm:px-4 py-2 hover:bg-[#22422e] transition-colors"
        >
          <span className="hidden xs:inline sm:inline">Enter to Win</span>
          <span className="xs:hidden sm:hidden">Enter</span>
          <ArrowRight size={14} />
        </a>
      </div>
    </header>
  );
}

function Hero({ spotsRemaining }) {
  return (
    <section
      data-testid="hero"
      id="enter"
      className="relative overflow-hidden hero-wash"
    >
      <div className="grain" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-8 pb-14 md:pt-20 md:pb-24">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-16 lg:items-center">
          {/* Left — copy + proof (order-2 on mobile so form comes first) */}
          <div className="reveal order-2 lg:order-none">
            <div className="hidden lg:inline-flex items-center gap-2 rounded-full bg-[#e8f0ea] text-[#22422e] px-3 py-1.5 eyebrow">
              <Sparkles size={12} />
              Giveaway — Limited Entries
            </div>

            <h1 className="hidden lg:block mt-5 font-heading text-4xl sm:text-5xl lg:text-[62px] leading-[1.02] font-semibold tracking-tight text-[#1f2937]">
              Win a <span className="relative inline-block">
                <span className="relative z-10 text-[#2f5a3f]">Year Supply</span>
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-1 h-3 bg-[#e8f0ea] -z-0 rounded"
                />
              </span>{" "}
              of Supplements.
            </h1>

            <p className="lg:mt-5 max-w-xl text-[16px] sm:text-[17px] leading-relaxed text-[#4b5563]">
              Choose any products you want from our curated catalog of premium wellness
              brands. Plus, get a <strong className="text-[#1f2937]">free jar of
              Young Again creatine</strong> just for entering.
            </p>

            {/* Bullet proofs */}
            <ul className="mt-5 lg:mt-6 grid sm:grid-cols-2 gap-y-2.5 gap-x-6 text-[14.5px] text-[#1f2937]">
              {[
                "1-year supply, any brand you choose",
                "Free creatine jar for every entry",
                "Premium brands like Momentous & Nordic Naturals",
                "No purchase necessary",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check size={16} className="mt-0.5 text-[#2f5a3f] shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            {/* Countdown + spots — only show desktop version here */}
            <div className="hidden lg:block mt-8">
              <div className="eyebrow text-[#4b5563] mb-2 flex items-center gap-1.5">
                <Clock size={12} /> Giveaway ends in
              </div>
              <CountdownTimer />
            </div>

            <div className="hidden lg:inline-flex mt-5 items-center gap-2 text-[13px] text-[#4b5563]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2f5a3f] opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2f5a3f]" />
              </span>
              Only <strong className="text-[#1f2937]">{spotsRemaining.toLocaleString()}</strong> free creatine jars left this month.
            </div>
          </div>

          {/* Right — mobile-first: badge + headline + form + countdown, then desktop floats */}
          <div className="relative reveal order-1 lg:order-none">
            {/* Mobile-only: eyebrow + headline stacked above form */}
            <div className="lg:hidden mb-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#e8f0ea] text-[#22422e] px-3 py-1.5 eyebrow">
                <Sparkles size={12} />
                Giveaway — Limited Entries
              </div>
              <h1 className="mt-4 font-heading text-[34px] xs:text-4xl leading-[1.04] font-semibold tracking-tight text-[#1f2937]">
                Win a{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-[#2f5a3f]">Year Supply</span>
                  <span
                    aria-hidden
                    className="absolute left-0 right-0 bottom-1 h-2.5 bg-[#e8f0ea] -z-0 rounded"
                  />
                </span>{" "}
                of Supplements.
              </h1>
            </div>

            {/* Floating hero images (desktop only) */}
            <div className="relative hidden lg:block">
              <div className="absolute -top-6 -right-6 w-64 h-64 rounded-3xl overflow-hidden shadow-2xl rotate-3 border-8 border-white">
                <img
                  src={BRAND_ASSETS.hero}
                  alt="Premium supplements"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-10 -left-8 w-44 h-44 rounded-3xl overflow-hidden shadow-xl -rotate-6 border-8 border-white bg-white flex items-center justify-center p-3">
                <img
                  src={BRAND_ASSETS.creatine_jar}
                  alt="Free creatine"
                  className="w-full h-full object-contain"
                />
                <div className="absolute bottom-2 left-2 right-2 rounded-full bg-[#2f5a3f] text-white text-[11px] font-semibold py-1.5 text-center">
                  FREE with entry
                </div>
              </div>
            </div>

            {/* Form card */}
            <div
              data-testid="hero-form-card"
              className="relative bg-white rounded-2xl border border-gray-100 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.18)] p-5 sm:p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-4 gap-2">
                <div className="min-w-0">
                  <h2 className="font-heading text-lg sm:text-xl font-semibold text-[#1f2937]">
                    Claim your entry
                  </h2>
                  <p className="text-[13px] sm:text-sm text-[#4b5563]">
                    Takes less than 30 seconds.
                  </p>
                </div>
                <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#e8f0ea] px-2.5 py-1.5 text-[11px] font-semibold text-[#22422e]">
                  <Gift size={12} /> Free gift
                </div>
              </div>
              <KlaviyoEmbed testId="hero-klaviyo-form" />
            </div>

            {/* Mobile-only: countdown + spots remaining BELOW form */}
            <div className="lg:hidden mt-6">
              <div className="eyebrow text-[#4b5563] mb-2 flex items-center gap-1.5">
                <Clock size={12} /> Giveaway ends in
              </div>
              <CountdownTimer compact />
              <div className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-[#4b5563]">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2f5a3f] opacity-70" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2f5a3f]" />
                </span>
                Only{" "}
                <strong className="text-[#1f2937]">
                  {spotsRemaining.toLocaleString()}
                </strong>{" "}
                jars left this month.
              </div>
            </div>
          </div>
        </div>

        {/* Mobile hero images */}
        <div className="mt-10 lg:hidden">
          <div className="eyebrow text-[#4b5563] mb-3 text-center">What's included</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src={BRAND_ASSETS.hero}
                alt="Premium supplements"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3] relative bg-white border border-[#ececec] flex items-center justify-center p-3">
              <img
                src={BRAND_ASSETS.creatine_jar}
                alt="Free creatine"
                className="w-full h-full object-contain"
                loading="lazy"
              />
              <div className="absolute bottom-1.5 left-1.5 right-1.5 rounded-full bg-[#2f5a3f] text-white text-[10px] font-semibold py-1 text-center">
                FREE with entry
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValueProps() {
  const items = [
    {
      icon: ShieldCheck,
      title: "Pharmaceutical-grade purity",
      body: "Every brand we carry is rigorously tested for potency, purity, and ingredient integrity.",
    },
    {
      icon: Leaf,
      title: "Curated, not crowded",
      body: "Hand-selected wellness brands — no fillers, no noise, just proven products that work.",
    },
    {
      icon: Truck,
      title: "Free US shipping on $79+",
      body: "30-day hassle-free returns. If it doesn't work for you, we make it right.",
    },
  ];

  return (
    <section
      data-testid="why-section"
      className="py-14 md:py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center reveal">
          <div className="eyebrow text-[#2f5a3f]">Why The Natural</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1f2937]">
            A trusted home for premium supplementation.
          </h2>
          <p className="mt-4 text-[17px] text-[#4b5563]">
            We exist to make wellness simple. Performance, longevity, gut health,
            recovery — all under one trusted roof.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {items.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              data-testid={`value-prop-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className="reveal group rounded-2xl border border-gray-100 bg-white p-7 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#e8f0ea] text-[#2f5a3f]">
                <Icon size={20} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[#1f2937]">
                {title}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[#4b5563]">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandsMarquee() {
  const loop = [...BRANDS, ...BRANDS];
  return (
    <section
      data-testid="brands-marquee"
      className="py-12 md:py-16 bg-[#fafaf5] border-y border-[#ececec]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 text-center mb-7 md:mb-8">
        <div className="eyebrow text-[#2f5a3f]">Featured Brands</div>
        <h3 className="mt-2 font-heading text-2xl md:text-3xl font-semibold text-[#1f2937]">
          The world's most trusted supplement makers — in one place.
        </h3>
      </div>

      <div className="marquee-mask">
        <div className="marquee-track">
          {loop.map((b, i) => (
            <div
              key={`${b.name}-${i}`}
              className="mx-5 md:mx-8 shrink-0 w-40 md:w-56"
              data-testid={`brand-tile-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-white border border-[#ececec] shadow-sm">
                <img
                  src={b.url}
                  alt={b.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-2 text-center font-heading text-sm font-medium text-[#1f2937]">
                {b.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Brands() {
  // Group A–Z
  const groups = ALL_BRANDS.reduce((acc, name) => {
    const letter = name[0].toUpperCase();
    const key = /[A-Z]/.test(letter) ? letter : "#";
    (acc[key] ||= []).push(name);
    return acc;
  }, {});
  const letters = Object.keys(groups).sort();

  return (
    <section data-testid="brands-directory" className="py-14 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 reveal">
          <div className="max-w-2xl">
            <div className="eyebrow text-[#2f5a3f]">Shop by Brand</div>
            <h2 className="mt-2 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#1f2937]">
              Pick from {ALL_BRANDS.length}+ premium wellness brands.
            </h2>
            <p className="mt-3 text-[16px] text-[#4b5563]">
              The winner chooses anything they want from the full catalog. From
              gut health to longevity, here's every brand on our shelves.
            </p>
          </div>
          <div className="hidden md:inline-flex items-center gap-2 text-sm text-[#4b5563]">
            <Award size={16} className="text-[#a96f44]" />
            {ALL_BRANDS.length}+ brands & growing
          </div>
        </div>

        {/* Featured brand tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-12 md:mb-14">
          {BRANDS.slice(0, 6).map((b) => (
            <div
              key={b.name}
              data-testid={`featured-brand-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="reveal group rounded-2xl overflow-hidden border border-[#ececec] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
            >
              <div className="aspect-[4/3] bg-white overflow-hidden">
                <img
                  src={b.url}
                  alt={b.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  loading="lazy"
                />
              </div>
              <div className="px-3 py-2.5 text-center">
                <p className="font-heading text-[13px] sm:text-sm font-semibold text-[#1f2937] truncate">
                  {b.name}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* A–Z jump nav */}
        <div
          data-testid="brand-az-nav"
          className="reveal flex flex-wrap gap-1.5 pb-6 border-b border-[#ececec]"
        >
          <span className="eyebrow text-[#4b5563] mr-2 self-center">Jump to</span>
          {letters.map((l) => (
            <a
              key={l}
              href={`#brand-${l}`}
              className="inline-flex h-8 min-w-[32px] items-center justify-center rounded-full border border-[#ececec] bg-white px-2 text-[13px] font-heading font-semibold text-[#22422e] hover:bg-[#e8f0ea] hover:border-[#c8dccf] transition-colors"
            >
              {l}
            </a>
          ))}
        </div>

        {/* A–Z directory */}
        <div className="mt-8 space-y-8 md:space-y-10">
          {letters.map((l) => (
            <div
              key={l}
              id={`brand-${l}`}
              data-testid={`brand-group-${l}`}
              className="reveal scroll-mt-24"
            >
              <div className="flex items-baseline gap-3 mb-3">
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#2f5a3f]">
                  {l}
                </h3>
                <span className="text-xs text-[#4b5563]">
                  {groups[l].length} {groups[l].length === 1 ? "brand" : "brands"}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {groups[l].map((name) => (
                  <div
                    key={name}
                    className="rounded-lg border border-[#ececec] bg-[#fafaf5] px-3 py-2.5 text-[13.5px] text-[#1f2937] hover:bg-[#e8f0ea] hover:border-[#c8dccf] transition-colors"
                  >
                    {name}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center reveal">
          <a
            href="#enter"
            data-testid="brands-bottom-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#2f5a3f] text-white font-heading font-semibold px-6 py-3 hover:bg-[#22422e] transition-colors"
          >
            Enter to win a year of any brand
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

function GiftCallout() {
  return (
    <section
      data-testid="gift-callout"
      className="relative py-14 md:py-24 bg-[#22422e] text-white overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-20"
        style={{
          background:
            "radial-gradient(600px 300px at 10% 10%, rgba(169,111,68,0.5), transparent 60%), radial-gradient(800px 400px at 90% 90%, rgba(47,90,63,0.8), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="reveal">
            <div className="eyebrow text-[#f4f4ed]/80">Everyone Wins Something</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              A free jar of creatine. <span className="text-[#e8f0ea]/80">On us.</span>
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-white/75 max-w-lg">
              Register now and claim a free tub of{" "}
              <strong className="text-white">Young Again Creatine Monohydrate</strong> —
              clinically-dosed at 5g per serving for strength, recovery, and
              cognitive health. While supplies last.
            </p>

            <ul className="mt-6 space-y-2.5 text-[15px] text-white/85">
              {[
                "5g creatine monohydrate per serving (30 servings)",
                "Supports strength, power output & muscle recovery",
                "Enhanced cognitive performance & mental clarity",
                "Backed by Young Again — 30+ years of honest formulas",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check size={16} className="mt-0.5 text-[#e8f0ea] shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <a
              href="#enter"
              data-testid="gift-callout-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-white text-[#22422e] hover:bg-[#f4f4ed] font-heading font-semibold px-6 py-3.5 transition-all"
            >
              Claim my free jar
              <ArrowRight size={16} />
            </a>
          </div>

          <div className="reveal relative">
            <div className="relative mx-auto w-full max-w-md aspect-square rounded-[32px] overflow-hidden bg-white border-8 border-white/10 shadow-2xl flex items-center justify-center p-6">
              <img
                src={BRAND_ASSETS.creatine_jar}
                alt="Young Again Creatine Monohydrate — free with entry"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="absolute -top-4 -right-2 md:-right-8 rotate-[6deg] rounded-full bg-[#a96f44] text-white px-4 py-2 text-sm font-heading font-semibold shadow-lg">
              $16.95 value — FREE
            </div>
            <div className="absolute -bottom-6 -left-2 md:-left-6 -rotate-[4deg] rounded-2xl bg-white text-[#22422e] px-4 py-3 shadow-xl max-w-[220px]">
              <div className="flex items-center gap-1 text-[#a96f44]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="text-[13px] mt-1 leading-snug">
                "Strength + cognitive boost in one scoop."
              </p>
              <p className="text-[11px] text-[#4b5563] mt-0.5">— Verified buyer</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialProof() {
  const badges = [
    { label: "1,200+ SKUs", Icon: Award },
    { label: "50,000+ customers", Icon: Heart },
    { label: "Secure checkout", Icon: ShieldCheck },
    { label: "30-day returns", Icon: Check },
  ];
  return (
    <section data-testid="social-proof" className="py-14 md:py-24 bg-[#fafaf5]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-2xl reveal">
          <div className="eyebrow text-[#2f5a3f]">Loved by thousands</div>
          <h2 className="mt-2 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#1f2937]">
            Real reviews from real wellness customers.
          </h2>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={t.name}
              data-testid={`testimonial-${i}`}
              className="reveal rounded-2xl bg-white border border-[#ececec] p-6 md:p-7 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-center gap-1 text-[#a96f44]">
                {[...Array(t.rating)].map((_, idx) => (
                  <Star key={idx} size={15} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <blockquote className="mt-3 text-[15px] leading-relaxed text-[#1f2937]">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-[#e8f0ea] text-[#2f5a3f] flex items-center justify-center font-heading font-semibold text-sm">
                  {t.name.charAt(0)}
                </div>
                <div className="text-sm font-medium text-[#1f2937]">{t.name}</div>
                <span className="text-xs text-[#4b5563]">· Verified buyer</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[#4b5563] text-sm reveal">
          {badges.map(({ label, Icon }) => (
            <div key={label} className="inline-flex items-center gap-2">
              <Icon size={16} className="text-[#2f5a3f]" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ spotsRemaining }) {
  return (
    <section
      id="final-cta"
      data-testid="final-cta"
      className="relative py-14 md:py-24 bg-white overflow-hidden pb-28 md:pb-24"
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 380px at 50% 0%, rgba(47,90,63,0.08), transparent 70%)",
        }}
      />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center reveal">
        <div className="eyebrow text-[#2f5a3f]">Last chance this month</div>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-[52px] leading-[1.05] font-semibold tracking-tight text-[#1f2937]">
          Enter now. Win a year of wellness.
        </h2>
        <p className="mt-4 text-[17px] text-[#4b5563] max-w-2xl mx-auto">
          Only{" "}
          <strong className="text-[#1f2937]">
            {spotsRemaining.toLocaleString()}
          </strong>{" "}
          free creatine jars remain. Winner announced at the end of the month.
        </p>

        <div className="mt-8 flex justify-center">
          <CountdownTimer className="max-w-md" />
        </div>

        <div className="mt-10 mx-auto max-w-md">
          <div className="rounded-2xl border border-gray-100 bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.18)] p-6 md:p-8 text-left">
            <KlaviyoEmbed testId="final-klaviyo-form" />
          </div>
        </div>
      </div>
    </section>
  );
}

function StickyMobileCTA({ show }) {
  return (
    <div
      data-testid="sticky-mobile-cta"
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 pb-[env(safe-area-inset-bottom)] ${
        show ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="mx-3 mb-3 rounded-2xl bg-[#22422e] text-white shadow-[0_20px_40px_-10px_rgba(0,0,0,0.4)] px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow text-white/70">Giveaway ends soon</div>
          <div className="font-heading text-sm font-semibold truncate">
            Win a year of supplements
          </div>
        </div>
        <a
          href="#enter"
          data-testid="sticky-mobile-cta-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white text-[#22422e] text-sm font-semibold px-4 py-2.5"
        >
          Enter
          <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#f4f4ed] border-t border-[#ececec]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-2">
            <img
              src={BRAND_ASSETS.site_logo}
              alt="The Natural"
              className="h-8 w-auto object-contain"
            />
          </div>
          <p className="text-xs text-[#4b5563] max-w-2xl">
            No purchase necessary. Void where prohibited. By entering, you agree to
            receive email and SMS marketing messages from The Natural. Reply STOP to
            opt out. Msg & data rates may apply. Full rules available on request.
            Winner announced at the end of the current month.
          </p>
          <div className="text-xs text-[#4b5563]">
            © {new Date().getFullYear()} The Natural
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Page ---------- */

export default function LandingPage() {
  useReveal();
  const stats = useStats();
  const { show } = useSticky();

  return (
    <main data-testid="landing-page" className="bg-white text-[#1f2937]">
      <NavBar />
      <Hero spotsRemaining={stats.spots_remaining} />
      <ValueProps />
      <BrandsMarquee />
      <Brands />
      <GiftCallout />
      <SocialProof />
      <FinalCTA spotsRemaining={stats.spots_remaining} />
      <Footer />
      <StickyMobileCTA show={show} />
    </main>
  );
}
